import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Spinner, Alert, InputGroup, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api/axios';
import { AuthContext } from '../../context/AuthContext';
import ForgotPasswordModal from '../../components/modals/ForgotPasswordModal';

function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successNotification, setSuccessNotification] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const currentVersion = import.meta.env.PACKAGE_VERSION || '1.0.4';

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/login', {
        email,
        password,
      });

      if (response.data.data && response.data.data.token) {
        const user = response.data.data.user;
        
        // Restrict Backoffice access to Admins only
        if (user.role !== 'Admin' && user.role !== 'SuperAdmin' && user.role !== 'admin') {
          setError(t('access_denied_admin_only', 'Acceso denegado. Este portal es exclusivo para el personal administrativo.'));
          setLoading(false);
          return;
        }

        login(user, response.data.data.token);
        if (user.must_change_password || user.requires_password_change) {
          navigate('/user-pages/force-password-change');
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(response.data.message || t('error', 'Error de inicio de sesión.'));
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError(t('invalid_credentials', 'Credenciales inválidas.'));
      } else {
        setError(t('server_connection_error', 'Ocurrió un error de conexión con el servidor.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex align-items-stretch auth h-100 w-100 m-0 p-0" style={{ backgroundColor: '#ffffff', minHeight: '100vh', width: '100%' }}>
        <div className="row flex-grow w-100 mx-0" style={{ minHeight: '100vh' }}>
          <div className="col-12 col-lg-6 d-flex align-items-center justify-content-center bg-white shadow-lg z-1 p-4 p-sm-5">
            <div className="text-start w-100" style={{ maxWidth: '440px' }}>
              
              {/* OFFICIAL RUTEX LOGO */}
              <div className="brand-logo mb-4 text-center text-sm-start">
                <img 
                  src="/images/rutex-logo.svg" 
                  alt="RUTEX Logo" 
                  style={{ height: '60px', width: 'auto', objectFit: 'contain' }} 
                  onError={(e) => { e.target.onerror = null; e.target.src = '/images/logo.svg'; }}
                />
              </div>

              <h3 className="fw-bold mb-1" style={{ color: '#00024f', letterSpacing: '-0.5px' }}>
                {t('welcome_back', '¡Bienvenido de nuevo!')}
              </h3>
              <p className="text-muted mb-4 small" style={{ fontSize: '0.92rem' }}>
                {t('happy_see_you', 'Inicia sesión con tu cuenta corporativa RUTEX para continuar.')}
              </p>
              
              {successNotification && (
                <Alert 
                  variant="success" 
                  dismissible 
                  onClose={() => setSuccessNotification('')} 
                  className="mb-4 border-0 fw-bold d-flex align-items-center shadow-sm"
                  style={{ borderRadius: '12px', backgroundColor: '#d1fae5', color: '#065f46', fontSize: '0.9rem' }}
                >
                  <i className="mdi mdi-check-circle me-2" style={{ fontSize: '1.4rem' }}></i>
                  <span>{successNotification}</span>
                </Alert>
              )}

              {error && (
                <Alert 
                  variant="danger" 
                  dismissible 
                  onClose={() => setError('')} 
                  className="mb-4 border-0 fw-bold d-flex align-items-center shadow-sm"
                  style={{ borderRadius: '12px', backgroundColor: '#fee2e2', color: '#991b1b', fontSize: '0.9rem' }}
                >
                  <i className="mdi mdi-alert-circle me-2" style={{ fontSize: '1.4rem' }}></i>
                  <span>{error}</span>
                </Alert>
              )}

              <Form onSubmit={handleLogin}>
                {/* EMAIL FIELD */}
                <Form.Group className="mb-3">
                  <Form.Label className="text-dark small fw-bold mb-1">
                    {t('email', 'Correo Electrónico')} *
                  </Form.Label>
                  <div className="login-input-wrapper">
                    <span className="login-input-icon">
                      <i className="mdi mdi-email-outline"></i>
                    </span>
                    <input 
                      type="email" 
                      className="login-input-control"
                      placeholder={t('email', 'ejemplo@rutex.com')} 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </Form.Group>
                
                {/* PASSWORD FIELD WITH EYE TOGGLE */}
                <Form.Group className="mb-3">
                  <Form.Label className="text-dark small fw-bold mb-1">
                    {t('password', 'Contraseña')} *
                  </Form.Label>
                  <div className="login-input-wrapper">
                    <span className="login-input-icon">
                      <i className="mdi mdi-lock-outline"></i>
                    </span>
                    <input 
                      type={showPassword ? "text" : "password"} 
                      className="login-input-control"
                      placeholder={t('password', '••••••••')} 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="login-input-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      <i className={showPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"}></i>
                    </button>
                  </div>
                </Form.Group>

                {/* FORGOT PASSWORD */}
                <div className="d-flex justify-content-end align-items-center mb-4 pt-1">
                  <a 
                    href="!#" 
                    onClick={e => { e.preventDefault(); setError(''); setSuccessNotification(''); setShowForgotModal(true); }} 
                    className="auth-link text-primary small fw-bold text-decoration-none"
                    style={{ color: '#183eeb' }}
                  >
                    {t('forgot_password', '¿Olvidó su contraseña?')}
                  </a>
                </div>

                {/* LOGIN BUTTON */}
                <div className="d-grid gap-2 mb-4">
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="py-3 fw-bold text-white shadow"
                    style={{ 
                      borderRadius: '12px', 
                      background: 'linear-gradient(135deg, #183eeb 0%, #00024f 100%)', 
                      border: 'none',
                      fontSize: '1rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : t('sign_in', 'INICIAR SESIÓN')}
                  </Button>
                </div>
              </Form>

              <div className="mt-4 pt-3 text-center border-top">
                <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>
                  {t('system_version_title', { version: currentVersion, defaultValue: `Sistema de Gestión Comercial v${currentVersion} por Alpinca y Lácteos Danny` })}
                </small>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE BRANDING BANNER */}
          <div className="col-lg-6 d-none d-lg-flex flex-column justify-content-between p-5 m-0" style={{ background: 'linear-gradient(135deg, #00024f 0%, #183eeb 100%)', color: '#ffffff', minHeight: '100vh' }}>
            <div className="d-flex align-items-center">
              <img src="/images/rutex-simbolo.svg" alt="RUTEX Symbol" style={{ height: '40px', filter: 'brightness(0) invert(1)' }} onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="ms-2 fw-bold h4 mb-0 text-white" style={{ letterSpacing: '2px' }}>RUTEX</span>
            </div>
            
            <div className="my-auto py-5 px-3">
              <h2 className="fw-bold mb-4" style={{ color: '#ffffff', lineHeight: 1.35, fontSize: '1.7rem' }}>
                {t('branding_quote_title', 'Rutex no se trata solo de trazar trayectos en un mapa, sino de optimizar el intercambio de valor. Nace de la eficiencia en el movimiento.')}
              </h2>
              <p className="text-white-50" style={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                {t('branding_quote_subtitle', 'La Ruta no solo mueve productos, mueve soluciones. El sufijo "Ex" es sumamente potente porque no tiene un solo significado, sino que evoca expansión y experiencia.')}
              </p>
            </div>

            <div className="small text-white-50 border-top border-secondary pt-3">
              &copy; {new Date().getFullYear()} RUTEX System. {t('all_rights_reserved', 'Todos los derechos reservados.')} | {t('system_version_title', { version: currentVersion, defaultValue: `Sistema de Gestión Comercial v${currentVersion} por Alpinca y Lácteos Danny` })}
            </div>
          </div>
        </div>
      </div>  

      {/* MODAL DE RECUPERACIÓN DE CONTRASEÑA (HU26) */}
      <ForgotPasswordModal
        show={showForgotModal}
        onHide={() => setShowForgotModal(false)}
        onSuccess={(message) => {
          setShowForgotModal(false);
          setSuccessNotification(message);
        }}
      />
    </>
  );
}

export default Login;